import {
  ADMIN_FILTERS,
  getClub,
  type AdminFilter,
  type AdminPlayerPage,
  type AdminPlayerRow,
} from "@clubap/shared";
import { useCallback, useEffect, useState } from "react";
import { fetchAdminPlayers, setPlayerDev, type PublicProfile } from "../api.js";
import { ClubMark } from "./ClubBadges.js";
import { setClubPresident } from "../social/api.js";

interface Props {
  /** Mi propio id: la fila propia se marca y no se ofrece administrar. */
  me: string;
  /** Abrir el perfil de alguien para cambiarlo o borrarlo. Lo monta App. */
  onManage: (profile: PublicProfile) => void;
  onClose: () => void;
}

const FILTER_LABELS: Record<AdminFilter, string> = {
  todas: "Todas",
  admin: "Admins",
  dev: "Dev",
  presidentes: "Presidentes",
  borradas: "Borradas",
};

function fecha(at: number): string {
  return new Date(at).toLocaleDateString();
}

/**
 * La vista de administración: quién existe y qué marcas tiene.
 *
 * Es una hoja ancha y no un panel lateral porque es lo único de la UI que se recorre
 * leyendo filas: los demás paneles enseñan una cosa a la vez —tu inventario, tu
 * conversación— y éste existe para comparar cuentas entre sí.
 *
 * DOS COSAS QUE ESTA VISTA NO HACE, Y NO SON OLVIDOS:
 *
 * · No reparte `admin`. La autoridad que se propaga sola no se deshace desde dentro;
 *   se sigue dando con `scripts/make-admin.mts`. La columna sólo lo muestra.
 * · No toca el saldo. El libro de monedas es de sólo agregar, y un campo editable
 *   aquí sería la forma más rápida de romper esa regla.
 */
export function AdminPanel({ me, onManage, onClose }: Props) {
  const [filter, setFilter] = useState<AdminFilter>("todas");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AdminPlayerPage | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await fetchAdminPlayers({ filter, q: query, page }));
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo cargar.");
    }
  }, [filter, query, page]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* Cambiar de filtro o de búsqueda vuelve a la primera página: quedarse en la
     séptima de una lista que ahora tiene dos se ve como una lista vacía. */
  const changeFilter = (next: AdminFilter) => {
    setFilter(next);
    setPage(1);
  };

  const toggleDev = async (row: AdminPlayerRow) => {
    setBusy(row.id);
    try {
      await setPlayerDev(row.id, !row.dev);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo cambiar.");
    } finally {
      setBusy(null);
    }
  };

  const appoint = async (row: AdminPlayerRow, clubId: string) => {
    setBusy(row.id);
    try {
      const preside = row.clubs.some(
        (entry) => entry.id === clubId && entry.role === "presidente",
      );
      await setClubPresident(clubId, preside ? null : row.id);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo nombrar.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="overlay">
      <div className="panel sheet sheet--admin">
        <div className="sheet__head">
          <h2 className="sheet__title">Administración</h2>
          <span className="sheet__coins">{data?.total ?? 0} cuentas</span>
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        {error && (
          <p className="sheet__error" role="alert">
            {error}
          </p>
        )}

        <div className="editor__row">
          <input
            className="join__input"
            value={query}
            placeholder="Buscar por nombre…"
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="auth__tabs" role="tablist">
          {ADMIN_FILTERS.map((each) => (
            <button
              key={each}
              className={`auth__tab${filter === each ? " auth__tab--on" : ""}`}
              type="button"
              role="tab"
              aria-selected={filter === each}
              onClick={() => changeFilter(each)}
            >
              {FILTER_LABELS[each]}
            </button>
          ))}
        </div>

        {!data && <p className="sheet__hint">Cargando…</p>}

        {data && data.rows.length === 0 && (
          <p className="sheet__hint">Ninguna cuenta cumple ese filtro.</p>
        )}

        {data && data.rows.length > 0 && (
          <ul className="sheet__list">
            {data.rows.map((row) => (
              <li className={`ficha${row.deletedAt ? " ficha--borrada" : ""}`} key={row.id}>
                <div className="ficha__cabecera">
                  <span className="ficha__nombre">{row.username}</span>
                  {row.id === me && <span className="sheet__badge">tú</span>}
                  {row.admin && <span className="sheet__badge">admin</span>}
                  {row.dev && <span className="sheet__badge">dev</span>}
                  {row.deletedAt && <span className="ficha__borrada">borrada {fecha(row.deletedAt)}</span>}
                </div>

                {/* El correo es el dato que justifica esta vista: sin él no se puede
                    atender "no me llega el enlace de recuperación". Y sin confirmar
                    NO recupera nada, así que el estado va pegado al correo. */}
                <p className="ficha__linea">
                  {row.email ? (
                    <>
                      <span className="ficha__correo">{row.email}</span>
                      <span className={row.emailVerified ? "ficha__si" : "ficha__no"}>
                        {row.emailVerified ? "✓ confirmado" : "sin confirmar"}
                      </span>
                    </>
                  ) : (
                    <span className="ficha__no">sin correo de recuperación</span>
                  )}
                </p>

                {row.clubs.length > 0 && (
                  <p className="ficha__linea">
                    {row.clubs.map((entry) => {
                      /* Un club puede haberse archivado con la cuenta dentro, así que el
                         catálogo puede no tenerlo: se dibuja el id crudo antes que nada. */
                      const club = getClub(entry.id);
                      if (!club) return null;

                      return (
                        <span
                          className="badge badge--named"
                          key={entry.id}
                          style={{ color: club.color, borderColor: club.color }}
                        >
                          <ClubMark club={club} />
                          <span className="badge__name">
                            {club.code}
                            {entry.role === "presidente" && " · preside"}
                          </span>
                        </span>
                      );
                    })}
                  </p>
                )}

                <p className="ficha__linea ficha__fechas">
                  Creada {fecha(row.createdAt)} · vista {fecha(row.lastSeenAt)}
                </p>

                {!row.deletedAt && (
                  <div className="ficha__acciones">
                    <button
                      className="social__quiet"
                      type="button"
                      disabled={busy === row.id}
                      onClick={() => void toggleDev(row)}
                    >
                      {row.dev ? "Quitar dev" : "Dar dev"}
                    </button>

                    {/*
                      Sólo se ofrecen los clubes en los que YA está.

                      La regla es que un presidente es un miembro con papel, así que
                      nombrar a alguien de un club al que no pertenece significaría
                      meterlo primero — y meter a alguien donde no pidió entrar es
                      peor que no poder nombrarlo desde aquí.
                    */}
                    {row.clubs.map((entry) => (
                      <button
                        className="social__quiet"
                        key={entry.id}
                        type="button"
                        disabled={busy === row.id}
                        title={`${getClub(entry.id)?.name ?? entry.id}`}
                        onClick={() => void appoint(row, entry.id)}
                      >
                        {entry.role === "presidente" ? "Destituir de" : "Nombrar en"}{" "}
                        {getClub(entry.id)?.code ?? entry.id}
                      </button>
                    ))}

                    {row.id !== me && (
                      <button
                        className="social__quiet"
                        type="button"
                        onClick={() =>
                          onManage({
                            id: row.id,
                            username: row.username,
                            color: row.color,
                            faculty: null,
                            program: null,
                            semester: null,
                            createdAt: row.createdAt,
                            /*
                             * La vista de administración lista cuentas, no perfiles: no
                             * trae la descripción y no debe fingir que sí. Nulo es "no
                             * la tengo", y el panel de edición la vuelve a pedir.
                             */
                            bio: null,
                          })
                        }
                      >
                        Editar cuenta
                      </button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {data && data.pages > 1 && (
          <div className="ficha__paginas">
            <button
              className="social__quiet"
              type="button"
              disabled={data.page <= 1}
              onClick={() => setPage((current) => current - 1)}
            >
              ‹ Anterior
            </button>
            <span className="sheet__hint">
              {data.page} de {data.pages}
            </span>
            <button
              className="social__quiet"
              type="button"
              disabled={data.page >= data.pages}
              onClick={() => setPage((current) => current + 1)}
            >
              Siguiente ›
            </button>
          </div>
        )}

        <p className="sheet__hint">
          La marca <strong>admin</strong> no se reparte desde aquí: se da con{" "}
          <code>scripts/make-admin.mts</code>, que exige tener el servidor delante.
        </p>
      </div>
    </div>
  );
}
